import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  ArrowRight,
  CheckCircle,
  Calendar,
  MessageCircle,
  Zap,
  Building2,
  Smartphone,
  FormInput
} from 'lucide-react'
import { useSolutions } from '../hooks/useSolutions'

export const SolutionsPage: React.FC = () => {
  const { t } = useTranslation('solutions')
  const solutions = useSolutions()

  const techHighlights = [
    {
      title: t('technology.highlights.dynamicForms.title'),
      description: t('technology.highlights.dynamicForms.description'),
      icon: FormInput,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      title: t('technology.highlights.ai.title'),
      description: t('technology.highlights.ai.description'),
      icon: Zap,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: t('technology.highlights.multiplatform.title'),
      description: t('technology.highlights.multiplatform.description'),
      icon: Smartphone,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: t('technology.highlights.docker.title'),
      description: t('technology.highlights.docker.description'),
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    }
  ]

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-kylix-background via-white to-kylix-lightgray">
        <div className="container">
          <div className="max-w-5xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              <span className="text-gray-900">{t('page.title.prefix')} </span>
              <span className="bg-gradient-to-r from-kylix-primary to-kylix-secondary bg-clip-text text-transparent">
                {t('page.title.highlight')}
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto"
            >
              {t('page.description')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <button className="btn-primary">
                <ArrowRight className="w-5 h-5" />
                {t('page.cta.explore')}
              </button>
              <Link to="/contact" className="btn-secondary">
                <Calendar className="w-5 h-5" />
                {t('page.cta.demo')}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 mb-4"
            >
              {t('products.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              {t('products.description')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={solution.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-8 rounded-2xl ${solution.bgColor} hover:shadow-xl transition-all duration-300 group`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-12 h-12 rounded-xl bg-white flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <solution.icon className={`w-6 h-6 ${solution.color}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{solution.title}</h3>
                    <p className="text-sm text-gray-600">{solution.subtitle}</p>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-6 leading-relaxed">
                  {solution.description}
                </p>

                <div className="space-y-2">
                  {solution.features.slice(0, 3).map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <CheckCircle className={`w-4 h-4 ${solution.color} flex-shrink-0`} />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                  {solution.features.length > 3 && (
                    <div className="text-sm text-gray-500 mt-2">
                      +{solution.features.length - 3} {t('products.labels.additionalFeatures')}
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-white/50">
                  <Link 
                    to={solution.id === 'bares' ? '/solutions/erp-bares' : `/solutions/${solution.id}`}
                    className="text-sm font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-2 group-hover:gap-3 transition-all"
                  >
                    {t('products.labels.seeDetails')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Highlights */}
      <section className="py-24 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 mb-4"
            >
              {t('technology.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              {t('technology.description')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {techHighlights.map((tech, index) => (
              <motion.div
                key={tech.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-16 h-16 ${tech.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <tech.icon className={`w-8 h-8 ${tech.color}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{tech.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{tech.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-kylix-primary to-kylix-secondary text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold mb-6"
            >
              {t('cta.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl mb-8 opacity-90"
            >
              {t('cta.description')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <Link to="/contact" className="px-8 py-4 bg-white text-kylix-primary font-bold rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {t('cta.buttons.personalizedDemo')}
              </Link>
              <Link to="/contact" className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-kylix-primary transition-colors inline-flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                {t('cta.buttons.talkSpecialist')}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}
